import { Badge, Flex, Heading } from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import { TrainingPerfPlots } from 'components/sections/admin-portal/machines/dialogs/training-performance/plots';
import { StackedText } from 'components/sections/admin-portal/machines/dialogs/training-performance/stacked-text';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DEFAULT_PLATE } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useEffect, useMemo } from 'react';

export const TrainingPerfShotsTable = () => {
  const loading = useTrainingPerformanceStore((s) => s.loading);

  const selectedShotIds = useTrainingPerformanceStore((s) => s.selectedShotIds);
  const selectedLog = useTrainingPerformanceStore((s) => s.selectedLog);
  const selectedIteration = useTrainingPerformanceStore(
    (s) => s.selectedIteration
  );

  const logShots = useTrainingPerformanceStore((s) => s.logShots);

  const shots = useMemo(() => {
    if (!selectedIteration) {
      // shouldn't trigger
      return [];
    }

    return logShots.filter((s) => selectedIteration.shotIDs.includes(s._id));
  }, [selectedIteration, logShots]);

  const columns = useMemo(() => {
    const getSpeedError = (shot: IMachineShot) => {
      const target =
        (selectedLog?.pitchPriority !== BuildPriority.Breaks
          ? selectedLog?.spinTarget?.vMph
          : selectedLog?.breaksTarget?.vMph) ?? 0;

      return TrajHelper.getSpeedFromTraj(shot.traj) - target;
    };

    const getBreakXError = (shot: IMachineShot) => {
      const target = selectedLog?.breaksTarget?.breakXIn ?? 0;
      return TrajHelper.getBreaksFromShot(shot).xInches - target;
    };

    const getBreakZError = (shot: IMachineShot) => {
      const target = selectedLog?.breaksTarget?.breakZIn ?? 0;
      return TrajHelper.getBreaksFromShot(shot).zInches - target;
    };

    const getSpinXError = (shot: IMachineShot) => {
      const target = selectedLog?.spinTarget?.wXRpm ?? 0;
      return (shot.bs?.wx ?? 0) - target;
    };

    const getSpinYError = (shot: IMachineShot) => {
      const target = selectedLog?.spinTarget?.wYRpm ?? 0;
      return (shot.bs?.wy ?? 0) - target;
    };

    const getSpinZError = (shot: IMachineShot) => {
      const target = selectedLog?.spinTarget?.wZRpm ?? 0;
      return (shot.bs?.wz ?? 0) - target;
    };

    const getPlateXError = (shot: IMachineShot) => {
      return TrajHelper.getPlateLoc(shot.traj).plate_x - DEFAULT_PLATE.plate_x;
    };

    const getPlateZError = (shot: IMachineShot) => {
      return TrajHelper.getPlateLoc(shot.traj).plate_z - DEFAULT_PLATE.plate_z;
    };

    const output: ITypedTableColumn<IMachineShot>[] = [
      {
        label: 'Shot',
        key: '_index',
        align: 'center',
        disableSort: true,
        formatFn: (_, rowIndex) => rowIndex + 1,
      },
      {
        label: 'Metric',
        key: 'metric',
        disableSort: true,
        formatFn: () => <StackedText line1="Error" line2="Value" />,
      },
      {
        label: 'Speed',
        subLabel: 'mph',
        key: 'speed',
        align: 'center',
        formatFn: (m) => (
          <StackedText
            line1={getSpeedError(m).toFixed(1)}
            line2={TrajHelper.getSpeedFromTraj(m.traj).toFixed(1)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aValue, bValue] = [a, b].map((m) => getSpeedError(m));
          return aValue <= bValue ? -dir : dir;
        },
      },
      {
        label: 'Break X',
        subLabel: 'in',
        key: 'breakXIn',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Breaks,
        formatFn: (m) => (
          <StackedText
            line1={getBreakXError(m).toFixed(1)}
            line2={TrajHelper.getBreaksFromShot(m).xInches.toFixed(1)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getBreakXError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Break Z',
        subLabel: 'in',
        key: 'breakZIn',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Breaks,
        formatFn: (m) => (
          <StackedText
            line1={getBreakZError(m).toFixed(1)}
            line2={TrajHelper.getBreaksFromShot(m).zInches.toFixed(1)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getBreakZError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Spin X',
        subLabel: 'rpm',
        key: 'wxRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        formatFn: (m) => (
          <StackedText
            line1={getSpinXError(m).toFixed(0)}
            line2={m.bs?.wx.toFixed(0)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getSpinXError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Spin Y',
        subLabel: 'rpm',
        key: 'wyRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        formatFn: (m) => (
          <StackedText
            line1={getSpinYError(m).toFixed(0)}
            line2={m.bs?.wy.toFixed(0)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getSpinYError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Spin Z',
        subLabel: 'rpm',
        key: 'wzRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        formatFn: (m) => (
          <StackedText
            line1={getSpinZError(m).toFixed(0)}
            line2={m.bs?.wz.toFixed(0)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getSpinZError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Plate X',
        subLabel: 'in',
        key: 'plateXIn',
        align: 'center',
        formatFn: (m) => (
          <StackedText
            line1={getPlateXError(m).toFixed(1)}
            line2={TrajHelper.getPlateLoc(m.traj).plate_x.toFixed(1)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getPlateXError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Plate Z',
        subLabel: 'in',
        key: 'plateZIn',
        align: 'center',
        formatFn: (m) => (
          <StackedText
            line1={getPlateZError(m).toFixed(1)}
            line2={TrajHelper.getPlateLoc(m.traj).plate_z.toFixed(1)}
          />
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getPlateZError(m));
          return aVal <= bVal ? -dir : dir;
        },
      },
    ];

    return output;
  }, [selectedLog]);

  // reset whenever shots changes
  useEffect(() => {
    useTrainingPerformanceStore.getState().resetSelectedShotIds();
  }, [shots]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminTrainingPerfShots,
    total: shots.length,
    enablePagination: true,
    pageSizes: TABLES.PAGE_SIZES.SM,
  };

  const checkable = useMemo(
    (): ITableCheckable => ({
      checkboxColumnIndex: 0,
      afterCheckOne: (id, checked) => {
        if (checked) {
          useTrainingPerformanceStore.getState().addSelectedShotIds([id]);
          return;
        }

        useTrainingPerformanceStore.getState().removeSelectedShotIds([id]);
      },
      afterCheckAll: (mode) => {
        switch (mode) {
          case 'all': {
            useTrainingPerformanceStore
              .getState()
              .addSelectedShotIds(shots.map((m) => m._id));
            break;
          }
          case 'none': {
            useTrainingPerformanceStore.getState().resetSelectedShotIds();
            break;
          }
          case 'some':
          default: {
            // shouldn't trigger
            break;
          }
        }
      },
      checkedActions: undefined,
    }),
    [selectedShotIds, shots]
  );

  if (!selectedLog) {
    return null;
  }

  if (!selectedIteration) {
    return null;
  }

  return (
    <ErrorBoundary identifier="TrainingPerfShotsTable">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <TrainingPerfPlots iterations={[selectedIteration]} />

        <Flex gap="2">
          <CommonBackArrow
            onClick={() =>
              useTrainingPerformanceStore.getState().setSelectedIteration(null)
            }
          />

          <Heading size="3">Iteration {selectedIteration.index + 1}</Heading>

          <Flex gap="1" align="center">
            <Badge color={RADIX.COLOR.NEUTRAL}>{shots.length} Shots</Badge>

            {selectedIteration.preset.spec.smartSampling && (
              <Badge color={RADIX.COLOR.SUCCESS}>Smart Sampling</Badge>
            )}

            {
              <Badge color={RADIX.COLOR.NEUTRAL}>
                PT Rel. Thresh.:{' '}
                {TrainingHelper.getIterationPtRelThreshold({
                  priority: selectedLog.pitchPriority,
                  ball_type: selectedLog.ball_type,
                  itr: selectedIteration,
                }).toFixed(2)}
              </Badge>
            }
          </Flex>
        </Flex>

        <CommonTableHoC
          identifier="TrainingPerfShotsTable"
          dataTestID="TrainingPerfShotsTable"
          displayColumns={columns}
          displayData={shots}
          loading={loading}
          {...pagination}
          {...checkable}
          enableSort
        />
      </Flex>
    </ErrorBoundary>
  );
};
